import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from 'lucide-react';
import {
  Button,
  Form,
  FormFieldSelectInput,
  LanguageInfo,
  Loader,
  ModalComponentWrapper,
} from '@/shared/components';
import { useDictionary } from '@/shared/api';
import {
  buildPeriodSelectOptions,
  useAppSettingsStore,
  useDateLocale,
  useDateModal,
  useLanguageOptions,
} from '@/shared/lib';
import { formatPeriodLabel } from '@/shared/utils';
import { DEFAULT_DATE_FORMAT, SUPPORTED_LANGUAGES } from '@/shared/config';
import type {
  DateFilter,
  HistoryDateFilter,
  HistoryDateFilterItem,
  HistoryDateFilterSelectItem,
  LanguageSelectOption,
} from '@/shared/types';
import {
  usePaidInvoicesForm,
  usePaidInvoicesListModal,
  usePaidInvoicesModal,
  type PaidInvoicesFormValues,
} from '../model';
import { buildPaidInvoicesParams, getDefaultDateFilter } from '../lib';

interface Props {
  onBack?: () => void;
  formValues?: PaidInvoicesFormValues;
  dateFrom?: Date;
  dateTo?: Date;
}

interface FormProps extends Props {
  historyDateFilters: HistoryDateFilterItem[];
}

const I18N_ROOT = 'personalAccount.userProfile.myDocuments.paidInvoices';

const EMPTY_HISTORY_DATE_FILTERS: HistoryDateFilterItem[] = [];

const PaidInvoicesForm = ({
  onBack,
  formValues,
  dateFrom,
  dateTo,
  historyDateFilters,
}: FormProps) => {
  const { t } = useTranslation();
  const { getLanguage } = useAppSettingsStore();
  const { dateLocale } = useDateLocale();
  const { languageOptions } = useLanguageOptions();
  const { openDateModal } = useDateModal();
  const { openPaidInvoicesModal } = usePaidInvoicesModal();
  const { openPaidInvoicesListModal } = usePaidInvoicesListModal();

  const form = usePaidInvoicesForm({
    language: getLanguage(),
    defaultDateFilter: getDefaultDateFilter(historyDateFilters),
    dateFrom,
    dateTo,
    formValues,
  });

  const customPeriodLabel = t(`${I18N_ROOT}.customPeriod`);

  const periodSelectOptions = useMemo(
    () =>
      buildPeriodSelectOptions([
        ...historyDateFilters,
        {
          datefilter: 'custom',
          type: 'client',
          name: customPeriodLabel,
          shortName: customPeriodLabel,
        },
      ]),
    [historyDateFilters, customPeriodLabel]
  );

  const getLabel = useCallback(
    (value: DateFilter) =>
      historyDateFilters.find(item => item.datefilter === value)?.name,
    [historyDateFilters]
  );

  const reopenForm = (dates?: { dateFrom?: Date; dateTo?: Date }) =>
    openPaidInvoicesModal({
      onBack,
      formValues: form.getValues(),
      dateFrom: dates?.dateFrom,
      dateTo: dates?.dateTo,
    });

  const handlePeriodSelect = (value: HistoryDateFilter) => {
    if (value !== 'custom') {
      form.setValue('datefrom', undefined);
      form.setValue('dateto', undefined);
      return;
    }

    openDateModal({
      onApply: (from?: Date, to?: Date) =>
        reopenForm({ dateFrom: from, dateTo: to }),
      onBack: () => reopenForm(),
    });
  };

  const onSubmit = (data: PaidInvoicesFormValues) => {
    openPaidInvoicesListModal({
      params: buildPaidInvoicesParams(data),
      onBack: () => openPaidInvoicesModal({ onBack, formValues: data }),
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
        <div className="flex flex-col gap-[1.6rem]">
          <FormFieldSelectInput<LanguageSelectOption>
            name="lang"
            label={t('common.language.placeholder')}
            items={languageOptions}
            disabled={SUPPORTED_LANGUAGES.length <= 1}
            renderItem={item => <LanguageInfo language={item} />}
            renderSelectedValue={value => (
              <LanguageInfo
                language={languageOptions.find(item => item.value === value)}
                className="gap-[0.8rem]"
                iconClassName="w-[2.4rem] h-[2.4rem]"
              />
            )}
          />

          <FormFieldSelectInput<HistoryDateFilterSelectItem>
            name="datefilter"
            label={t(`${I18N_ROOT}.dateFilterPlaceholder`)}
            items={periodSelectOptions}
            renderItem={item => (
              <div className="headline4 flex items-center justify-between w-full">
                {item.name}
                {item.value === 'custom' && (
                  <ChevronRightIcon className="size-8 text-tertiary" />
                )}
              </div>
            )}
            renderSelectedValue={value => (
              <div className="headline4">
                {value === 'custom'
                  ? formatPeriodLabel({
                      locale: dateLocale,
                      dateFrom: form.getValues('datefrom'),
                      dateTo: form.getValues('dateto'),
                      defaultLabel: customPeriodLabel,
                      dateFormat: DEFAULT_DATE_FORMAT,
                    })
                  : getLabel(value as DateFilter)}
              </div>
            )}
            showOptionCheckmark={false}
            onPointerDown={value =>
              handlePeriodSelect(value as HistoryDateFilter)
            }
          />

          <Button
            size="lg"
            variant="secondary"
            type="submit"
            className="mt-[1.6rem]">
            {t(`${I18N_ROOT}.showButton`)}
          </Button>
        </div>
      </form>
    </Form>
  );
};

const PaidInvoicesModal = (props: Props) => {
  const { t } = useTranslation();

  const { data: historyDateFilters, isLoading } = useDictionary<
    HistoryDateFilterItem[]
  >({ dictionaryName: 'historydatefilters' });

  return (
    <ModalComponentWrapper
      title={t(`${I18N_ROOT}.title`)}
      onBack={props.onBack}>
      {isLoading ? (
        <Loader />
      ) : (
        <PaidInvoicesForm
          {...props}
          historyDateFilters={
            historyDateFilters?.value ?? EMPTY_HISTORY_DATE_FILTERS
          }
        />
      )}
    </ModalComponentWrapper>
  );
};

export { PaidInvoicesModal };
